function t(v) {
    return String(v ?? "").trim()
}

/** Same required fields as `ShippingSection` address form. */
export function isShippingAddressComplete(v) {
    return (
        t(v.country) !== "" &&
        t(v.firstName) !== "" &&
        t(v.lastName) !== "" &&
        t(v.province) !== "" &&
        t(v.city) !== "" &&
        t(v.streetAddress) !== "" &&
        t(v.postalCode) !== "" &&
        t(v.phone) !== ""
    )
}

/**
 * Pickup with a chosen location skips the address form; otherwise require a
 * full shipping address.
 * @param {Record<string, unknown>} shipping
 * @param {{ method: string, pickupLocationId: number | null }} delivery
 */
export function isShippingSectionComplete(shipping, delivery) {
    if (delivery.method === "pickup" && delivery.pickupLocationId != null) {
        return true
    }
    return isShippingAddressComplete(shipping)
}

export function isContactSectionComplete(contact) {
    return (
        t(contact.firstName) !== "" &&
        t(contact.lastName) !== "" &&
        t(contact.email) !== "" &&
        t(contact.phone) !== "" &&
        t(contact.licenseNumber) !== ""
    )
}

export function isDeliverySectionComplete(delivery) {
    if (delivery.method === "pickup") {
        return delivery.pickupLocationId != null
    }
    return true
}

export function isBillingSectionComplete(billing) {
    return (
        t(billing.firstName) !== "" &&
        t(billing.lastName) !== "" &&
        t(billing.country) !== "" &&
        t(billing.province) !== "" &&
        t(billing.city) !== "" &&
        t(billing.streetAddress) !== "" &&
        t(billing.postalCode) !== "" &&
        t(billing.phone) !== "" &&
        t(billing.email) !== ""
    )
}

/** Pinned drop-off fields (location + date); license lives on Contact. */
export function isDropoffSectionComplete(dropoff) {
    return dropoff.locationId != null && t(dropoff.date) !== ""
}

/**
 * When hold-for-pickup, processing instructions are skipped; only boxes required.
 * @param {Record<string, unknown>} processing
 * @param {{ holdForPickup?: boolean }} [opts]
 */
export function isProcessingSectionComplete(processing, opts = {}) {
    if (opts.holdForPickup) {
        return t(processing.containers) !== ""
    }
    const rows = processing.instructions ?? []
    if (rows.length === 0) {
        return false
    }
    return rows.every((i) => i.speciesId != null && i.processId != null)
}

/**
 * Review has no inputs; “complete” means every prior step satisfies its
 * required fields.
 * @param {{
 *   contact: Record<string, unknown>,
 *   delivery: { method: string, pickupLocationId: number | null },
 *   shipping: Record<string, unknown>,
 *   billing: Record<string, unknown>,
 *   dropoff: Record<string, unknown>,
 *   processing: Record<string, unknown>,
 * }} model
 */
export function isReviewSectionComplete(model) {
    return (
        isContactSectionComplete(model.contact) &&
        isDeliverySectionComplete(model.delivery) &&
        isShippingSectionComplete(model.shipping, model.delivery) &&
        isBillingSectionComplete(model.billing) &&
        isDropoffSectionComplete(model.dropoff) &&
        isProcessingSectionComplete(model.processing, {
            holdForPickup: Boolean(model.delivery?.holdForPickup),
        })
    )
}
